import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useT } from "../../i18n/I18nContext";
import { apiClient } from "../../util/apiClient";
import { logger } from "../../util/logger";

// How long the user has to wait between two ``Send reset email``
// clicks. Has to be ≥ the backend rate-limit window
// (3/min per email — see request_password_reset in views_pkg/auth.py)
// to avoid letting users spam the button into a 429 lockout.
const REQUEST_COOLDOWN_SECONDS = 30;

// Self-service password reset. Two phases inside the same panel:
//
//   1. ``request`` — user enters their email; backend mints a reset
//      token (valid 1h) and emails it via SES. The endpoint returns
//      the same response whether or not the email exists, so we
//      can't (and won't) tell the user "no such account".
//   2. ``confirm`` — user pastes the token from the email and picks
//      a new password. On success they go back to the login form.
//
// Wires to the Django endpoints under ``/api/auth/password-reset/*``.
//
// IMPORTANT: this component renders inside react-toastify's portal,
// which can mount the toast under a ToastContainer that lives
// *outside* the React Router tree — calling ``useNavigate`` here
// throws "useNavigate() may be used only in the context of a <Router>
// component." The caller (Login.jsx) owns the routing and passes
// ``onRegisterClick`` so we never reach for router context here.
export default function Reset({ onDismiss, onRegisterClick }) {
  const t = useT();
  const [phase, setPhase] = useState("request");
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Cooldown is the remaining seconds before the user can hit
  // "Send reset email" again. Held in state so the countdown
  // re-renders the button label every second.
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimer = useRef(null);

  useEffect(() => () => {
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
  }, []);

  const startCooldown = () => {
    setCooldown(REQUEST_COOLDOWN_SECONDS);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          clearInterval(cooldownTimer.current);
          cooldownTimer.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const handleRegisterClick = () => {
    if (onDismiss) onDismiss();
    if (typeof onRegisterClick === "function") {
      onRegisterClick();
    } else {
      // Fallback when the caller forgot to wire it. A full-page
      // navigation is acceptable here since the Reset modal is a
      // dead-end anyway once the user has decided to register.
      window.location.href = "/register";
    }
  };

  const requestReset = async (event) => {
    if (event) event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || submitting || cooldown > 0) return;
    setSubmitting(true);
    try {
      await apiClient.post("/api/auth/password-reset/request/", {
        email: trimmed,
      });
      toast.success(t("auth.reset.sent"), { autoClose: 8000 });
      startCooldown();
      setPhase("confirm");
    } catch (err) {
      logger.warn("password_reset_request_failed", err?.message);
      const code = err?.response?.data?.error;
      const status = err?.response?.status;
      // ``django-ratelimit`` returns 403 with a generic body, so we
      // detect it by status as well as by error code.
      if (code === "rate_limited" || status === 429 || status === 403) {
        toast.error(t("auth.reset.rate_limited"));
        startCooldown();
      } else {
        toast.error(t("auth.reset.request_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmReset = async (event) => {
    if (event) event.preventDefault();
    if (!email.trim() || token.length !== 6 || !newPassword || submitting) return;
    setSubmitting(true);
    try {
      await apiClient.post("/api/auth/password-reset/confirm/", {
        email: email.trim(),
        token: token.trim(),
        new_password: newPassword,
      });
      toast.success(t("auth.reset.confirmed"), { autoClose: 6000 });
      if (onDismiss) onDismiss();
    } catch (err) {
      logger.warn("password_reset_confirm_failed", err?.message);
      const code = err?.response?.data?.error;
      const rules = err?.response?.data?.rules;
      if (code === "invalid_or_expired_token") {
        toast.error(t("auth.reset.invalid_token"));
      } else if (code === "weak_password" && Array.isArray(rules) && rules.length) {
        toast.error(`${t("auth.reset.weak_password")} ${rules.join(", ")}`);
      } else {
        toast.error(t("auth.reset.confirm_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Shared input chrome — pill-rounded to match the form pattern
  // on /setting and /contact (and now Login + SignUp) so the auth
  // flow reads as one component vocabulary across the whole app.
  const inputClass =
    "w-full px-4 py-3 rounded-full border border-border bg-surface text-text text-[16px]/[24px] placeholder:text-secondText/70 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition";

  return (
    // ``w-full`` lets the panel fill the toast portal (which is
    // ~360-400px wide on mobile by react-toastify defaults). The
    // previous ``w-50`` wasn't a real Tailwind class — it
    // collapsed to content width and made the modal look broken
    // on narrow screens.
    <div className="w-full">
      <div className="bg-surface rounded-lg p-5 sm:p-6 app-surface">
        <h2 className="text-lg font-semibold text-text mb-1">
          {t("auth.reset.title")}
        </h2>
        <p className="text-xs text-secondText mb-4 leading-relaxed">
          {phase === "request"
            ? t("auth.reset.request_hint")
            : t("auth.reset.confirm_hint")}
        </p>

        {phase === "request" ? (
          <form onSubmit={requestReset} noValidate className="space-y-3">
            <label htmlFor="reset-email" className="sr-only">
              {t("auth.email_label")}
            </label>
            <input
              id="reset-email"
              name="email"
              type="email"
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.reset.placeholder")}
              autoComplete="email"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- focus is intentional on this modal/form's primary input
              autoFocus
              required
            />
            <button
              type="submit"
              disabled={submitting || !email.trim() || cooldown > 0}
              className="app-button-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting
                ? t("common.loading")
                : cooldown > 0
                  ? t("auth.reset.cooldown", { seconds: cooldown })
                  : t("auth.reset.send")}
            </button>
            <button
              type="button"
              onClick={() => setPhase("confirm")}
              className="w-full text-sm text-secondText hover:text-primary py-1"
            >
              {t("auth.reset.have_token")}
            </button>
          </form>
        ) : (
          <form onSubmit={confirmReset} noValidate className="space-y-3">
            <label htmlFor="reset-email-confirm" className="sr-only">
              {t("auth.email_label")}
            </label>
            <input
              id="reset-email-confirm"
              name="email"
              type="email"
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.reset.placeholder")}
              autoComplete="email"
              required
            />
            <label htmlFor="reset-token" className="sr-only">
              {t("auth.reset.token_label")}
            </label>
            <input
              id="reset-token"
              name="token"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              className="w-full px-4 py-3 rounded-full border border-border bg-surface text-text font-mono tracking-[0.4em] text-center text-lg focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition"
              value={token}
              onChange={(e) =>
                // Strip non-digits as the user types so a paste from
                // mail clients that wrap the code in punctuation just
                // works.
                setToken(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder={t("auth.reset.token_placeholder")}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- focus is intentional on this modal/form's primary input
              autoFocus
              required
            />
            <label htmlFor="reset-new-password" className="sr-only">
              {t("auth.reset.new_password_label")}
            </label>
            <input
              id="reset-new-password"
              name="new_password"
              type="password"
              className={inputClass}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t("auth.reset.new_password_placeholder")}
              autoComplete="new-password"
              minLength={8}
              required
            />
            <button
              type="submit"
              disabled={
                submitting || !email.trim() || token.length !== 6 || !newPassword
              }
              className="app-button-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? t("common.loading") : t("auth.reset.confirm")}
            </button>
            <button
              type="button"
              onClick={() => setPhase("request")}
              disabled={cooldown > 0}
              className="w-full text-sm text-secondText hover:text-primary py-1 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:text-secondText"
            >
              {cooldown > 0
                ? t("auth.reset.cooldown", { seconds: cooldown })
                : t("auth.reset.resend")}
            </button>
          </form>
        )}

        <p className="mt-4 text-xs text-secondText text-center">
          {t("auth.reset.no_account")}{" "}
          <button
            type="button"
            onClick={handleRegisterClick}
            className="text-primary font-medium hover:underline"
          >
            {t("auth.reset.register")}
          </button>{" "}
          {t("auth.reset.now")}
        </p>
      </div>
    </div>
  );
}
